import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { fetchUsers } from '../../services/usersApi';
import axiosInstance from '../../services/axiosInstance';

export default function BranchDetailsModal({ branch, isOpen, onClose, isSuperadmin = false }) {
    const [activeTab, setActiveTab] = useState('info');
    const [users, setUsers] = useState([]);
    const [inventory, setInventory] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen && branch) {
            loadBranchDetails();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, branch]);

    const loadBranchDetails = async () => {
        setLoading(true);
        try {
            // Fetch users for this branch
            const usersRes = await fetchUsers(isSuperadmin);
            const allUsers = usersRes.data.data || usersRes.data || [];
            const branchId = String(branch.id);
            setUsers(
                allUsers.filter((u) => {
                    const userBranchId = u.branch_id ?? u.branch?.id;
                    return userBranchId != null && String(userBranchId) === branchId;
                })
            );

            // Fetch inventory for this branch (use branch_id)
            const inventoryEndpoint = isSuperadmin ? `/superadmin/inventory?branch_id=${branch.id}` : `/admin/inventory?branch_id=${branch.id}`;
            const inventoryRes = await axiosInstance.get(inventoryEndpoint);
            setInventory(inventoryRes.data.data || inventoryRes.data || []);
        } catch (error) {
            console.error('Error loading branch details:', error);
            setUsers([]);
            setInventory([]);
        }
        setLoading(false);
    };

    if (!branch) return null;

    const tabs = [
        { id: 'info', label: 'Branch Info' },
        { id: 'users', label: 'Users' },
    ];

    return (
        <Dialog open={isOpen} onClose={onClose} className="fixed z-50 inset-0 overflow-y-auto">
            <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
            <div className="flex items-center justify-center min-h-screen px-4">
                <Dialog.Panel className="relative bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-auto z-50">
                    {/* Header */}
                    <div className="flex justify-between items-center px-6 py-4 border-b">
                        <Dialog.Title className="text-xl font-bold text-gray-900">
                            Branch Details: {branch.name}
                        </Dialog.Title>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition"
                        >
                            <XMarkIcon className="w-6 h-6" />
                        </button>
                    </div>

                    {/* Tabs */}
                    <div className="flex border-b px-6">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`px-4 py-3 text-sm font-medium transition ${activeTab === tab.id
                                    ? 'border-b-2 border-blue-600 text-blue-600'
                                    : 'text-gray-500 hover:text-gray-700'
                                    }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Content */}
                    <div className="px-6 py-4 max-h-96 overflow-y-auto">
                        {loading ? (
                            <div className="text-center py-12 text-gray-500">Loading...</div>
                        ) : (
                            <>
                                {/* Branch Info Tab */}
                                {activeTab === 'info' && (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="text-sm font-semibold text-gray-600">Branch Name</label>
                                            <p className="text-base text-gray-900">{branch.name}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-600">Location/Address</label>
                                            <p className="text-base text-gray-900">{branch.location || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-600">Status</label>
                                            <p>
                                                <span
                                                    className={`inline-block px-3 py-1 rounded text-sm font-medium ${branch.status === 'ACTIVE'
                                                        ? 'bg-green-100 text-green-700'
                                                        : 'bg-gray-200 text-gray-500'
                                                        }`}
                                                >
                                                    {branch.status}
                                                </span>
                                            </p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-600">Date Created</label>
                                            <p className="text-base text-gray-900">{branch.createdAt ? new Date(branch.createdAt).toLocaleString() : 'N/A'}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-600">Last Updated</label>
                                            <p className="text-base text-gray-900">{branch.updatedAt ? new Date(branch.updatedAt).toLocaleString() : 'N/A'}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-600">Assigned Manager/Staff</label>
                                            <p className="text-base text-gray-900">{
                                                users.length > 0
                                                    ? users
                                                        .filter((u) => {
                                                            const role = (u.role || '').toLowerCase();
                                                            return role === 'cashier' || u.user_type === 2;
                                                        })
                                                        .map((u) => u.username)
                                                        .join(', ') || 'N/A'
                                                    : 'N/A'
                                            }</p>
                                        </div>
                                    </div>
                                )}

                                {/* Users Tab */}
                                {activeTab === 'users' && (
                                    <div>
                                        {users.length === 0 ? (
                                            <p className="text-center py-8 text-gray-500">No users assigned to this branch.</p>
                                        ) : (
                                            <table className="w-full text-sm border-collapse border">
                                                <thead className="bg-gray-100">
                                                    <tr>
                                                        <th className="px-4 py-2 text-left border">Username</th>
                                                        <th className="px-4 py-2 text-left border">Email</th>
                                                        <th className="px-4 py-2 text-left border">Role</th>
                                                        <th className="px-4 py-2 text-left border">Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {users.map(user => (
                                                        <tr key={user.id} className="hover:bg-gray-50">
                                                            <td className="px-4 py-2 border">{user.username}</td>
                                                            <td className="px-4 py-2 border">{user.email}</td>
                                                            <td className="px-4 py-2 border">{user.role_name || user.role || (user.user_type === 2 ? 'user' : 'N/A')}</td>
                                                            <td className="px-4 py-2 border">
                                                                <span
                                                                    className={`inline-block px-2 py-1 rounded text-xs font-medium ${user.status === 'ACTIVE'
                                                                        ? 'bg-green-100 text-green-600'
                                                                        : 'bg-gray-200 text-gray-500'
                                                                        }`}
                                                                >
                                                                    {user.status}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                )}

                            </>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end px-6 py-4 border-t">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition"
                        >
                            Close
                        </button>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
}
